/**
 * Bower-owned first-touch attribution + visitor identity.
 *
 * Why this exists separately from PostHog's `$initial_*` properties:
 *
 *   1. PostHog runs in feature-flags-only mode before cookie consent
 *      is granted. In that mode it never autocaptures pageviews, so
 *      `$initial_referrer` / `$initial_utm_*` / `$initial_current_url`
 *      are never set. Visitors who never accept the banner produce
 *      `User Signed Up` events with empty first-touch fields.
 *
 *   2. PostHog is widely blocked by uBlock Origin, Brave Shields,
 *      AdGuard, and corporate firewalls. Visitors behind those see
 *      the same empty first-touch fields even on default settings.
 *
 *   3. Even when posthog-js loads, `posthog.get_distinct_id()` only
 *      works *after* the SDK has initialised — which we defer to
 *      `requestIdleCallback` to keep it off the LCP path. A visitor
 *      who lands on `/auth` and signs up within the first 100 ms
 *      would see an undefined distinct_id.
 *
 * Solution: capture our own first-touch + visitor identity into our
 * own localStorage key, synchronously on the very first page render,
 * before React mounts. Independent of any third-party script. Then
 * the signup hook treats PostHog as a *fallback* enrichment source,
 * not the primary one.
 *
 * Storage shape (single key — easier to inspect, atomic-ish updates):
 *   localStorage['bower_first_touch_v1'] = JSON.stringify({
 *     visitorId,           // anonymous-but-stable across same-browser visits
 *     firstSeenAt,         // ISO timestamp of first ever pageview
 *     firstReferrer,
 *     firstReferringDomain,
 *     firstLandingUrl,
 *     firstLandingPathname,
 *     firstUtm: { source, medium, campaign, content, term },
 *     firstClickIds: { gclid, fbclid, msclkid, gad_source },
 *   })
 *
 * Session identity lives in sessionStorage (per-tab, per-session by
 * design — matches how posthog-js scopes `$session_id`).
 */

const STORAGE_KEY = 'bower_first_touch_v1';
const SESSION_KEY = 'bower_session_id_v1';

export interface BowerFirstTouch {
  /** Anonymous visitor id — stable across visits in the same browser. */
  visitorId: string;
  firstSeenAt: string;
  firstReferrer?: string;
  firstReferringDomain?: string;
  firstLandingUrl?: string;
  firstLandingPathname?: string;
  firstUtm: {
    source?: string;
    medium?: string;
    campaign?: string;
    content?: string;
    term?: string;
  };
  firstClickIds: {
    gclid?: string;
    fbclid?: string;
    msclkid?: string;
    gadSource?: string;
  };
}

const safeRandomId = (): string => {
  // crypto.randomUUID is in every browser we support (last 4 years).
  // Falls back to Math.random where unavailable (very old browsers,
  // SSR shims) — collisions are tolerable for an opaque visitor id.
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // ignore
  }
  return `bvt-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
};

const safeLocalStorageGet = (key: string): string | null => {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    // Private browsing / Storage access blocked
    return null;
  }
};

const safeLocalStorageSet = (key: string, value: string): void => {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    // Quota / private browsing — accept loss.
  }
};

const safeSessionStorageGet = (key: string): string | null => {
  try {
    return globalThis.sessionStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const safeSessionStorageSet = (key: string, value: string): void => {
  try {
    globalThis.sessionStorage?.setItem(key, value);
  } catch {
    // ignore
  }
};

const parseStored = (raw: string | null): BowerFirstTouch | null => {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof (parsed as BowerFirstTouch).visitorId === 'string'
    ) {
      // Backfill missing nested objects on records written by older
      // versions — happens when the schema grows fields between
      // releases. Never throws; returns a record the rest of the
      // code can safely read.
      const rec = parsed as Partial<BowerFirstTouch> & { visitorId: string };
      return {
        visitorId: rec.visitorId,
        firstSeenAt: rec.firstSeenAt ?? new Date().toISOString(),
        firstReferrer: rec.firstReferrer,
        firstReferringDomain: rec.firstReferringDomain,
        firstLandingUrl: rec.firstLandingUrl,
        firstLandingPathname: rec.firstLandingPathname,
        firstUtm: rec.firstUtm ?? {},
        firstClickIds: rec.firstClickIds ?? {},
      };
    }
  } catch {
    // Corrupt JSON — treat as missing, the caller will write a fresh
    // record below.
  }
  return null;
};

const extractParamFromCurrentUrl = (name: string): string | undefined => {
  try {
    const params = new URLSearchParams(globalThis.location?.search ?? '');
    const value = params.get(name);
    return value ? value : undefined;
  } catch {
    return undefined;
  }
};

const parseReferringDomain = (referrer: string | undefined): string | undefined => {
  if (!referrer) return undefined;
  try {
    return new URL(referrer).hostname || undefined;
  } catch {
    return undefined;
  }
};

/**
 * Initialise (or restore) the first-touch record. Call exactly once
 * before React mounts so the visitor id + first-touch snapshot exists
 * the instant any component asks for it.
 *
 * Idempotent: re-runs reuse the existing visitor id and first-touch
 * snapshot. Only the visitor id is regenerated if missing.
 */
export const initBowerFirstTouch = (): BowerFirstTouch => {
  const existing = parseStored(safeLocalStorageGet(STORAGE_KEY));
  if (existing) return existing;

  // First-ever pageview — capture everything we can see right now.
  const referrer = typeof document !== 'undefined' ? document.referrer || undefined : undefined;
  const href = typeof globalThis.location !== 'undefined' ? globalThis.location.href : undefined;
  const pathname =
    typeof globalThis.location !== 'undefined' ? globalThis.location.pathname : undefined;

  const record: BowerFirstTouch = {
    visitorId: safeRandomId(),
    firstSeenAt: new Date().toISOString(),
    firstReferrer: referrer,
    firstReferringDomain: parseReferringDomain(referrer),
    firstLandingUrl: href,
    firstLandingPathname: pathname,
    firstUtm: {
      source: extractParamFromCurrentUrl('utm_source'),
      medium: extractParamFromCurrentUrl('utm_medium'),
      campaign: extractParamFromCurrentUrl('utm_campaign'),
      content: extractParamFromCurrentUrl('utm_content'),
      term: extractParamFromCurrentUrl('utm_term'),
    },
    firstClickIds: {
      gclid: extractParamFromCurrentUrl('gclid'),
      fbclid: extractParamFromCurrentUrl('fbclid'),
      msclkid: extractParamFromCurrentUrl('msclkid'),
      gadSource: extractParamFromCurrentUrl('gad_source'),
    },
  };

  safeLocalStorageSet(STORAGE_KEY, JSON.stringify(record));
  return record;
};

/**
 * Read the first-touch record. Returns `null` when storage is
 * unavailable AND init was never called (e.g. unit tests). In normal
 * app usage this never returns null because `initBowerFirstTouch` is
 * called from main.tsx before any consumer.
 */
export const getBowerFirstTouch = (): BowerFirstTouch | null =>
  parseStored(safeLocalStorageGet(STORAGE_KEY));

/**
 * Per-tab session id. Survives navigations within the tab, resets on
 * tab close — matches posthog-js `$session_id` semantics. Lazily
 * generated on first access; safe to call from many components.
 */
export const getBowerSessionId = (): string => {
  const existing = safeSessionStorageGet(SESSION_KEY);
  if (existing) return existing;
  const fresh = safeRandomId();
  safeSessionStorageSet(SESSION_KEY, fresh);
  return fresh;
};
